'use client';

import React, { Suspense } from 'react';
import type { VisionsSpecial } from '@/types';
import Link from 'next/link';
import { RefreshCw } from 'lucide-react';
import { MatrixText } from '@/components/ui/matrix-text';
import PaintingPreview from './paintingpreview';

// Import extracted components
import { DashedLabel, DashedPrice } from './visions/HelperComponents';
import { ImageSlider } from './visions/ImageSlider';
import { GalleryView } from './visions/GalleryView';
import { FullscreenImageViewer } from './visions/FullscreenImageViewer';
import { useVisionsState } from './visions/useVisionsState';

interface VisionsSpecialProps {
  layout: VisionsSpecial;
}

export const Visions_Special: React.FC<VisionsSpecialProps> = ({ layout }) => {
  const {
    artImages,
    visionsVideos,
    currentImageIndex,
    fullscreenImage,
    showAllSection,
    currentVideoIndex,
    isVideoLoading,
    isChanging,
    isFullscreen,
    showZoomHint,
    videoRef,
    setFullscreenImage,
    handleRandomVideo,
    toggleFullscreen,
  } = useVisionsState(layout);

  const openFullscreen = (imageSrc: string) => {
    setFullscreenImage(imageSrc);
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
  };

  return (
    <section id="vision-special-section" className="font-mono mb-4">
      {layout.showChapter && (
        <>
          {/* Chapter Title */}
          <div className="p-[15px] rounded-md bg-[#ACA280] text-black mb-[12px] font-light font-mono text-[12px] uppercase flex flex-row justify-between relative">
            <span>{layout.chapterTitle}</span>
            <span>{layout.chapterInfo}</span>
          </div>

          <div className="p-[15px] rounded-md bg-white text-black mb-[12px] pb-2 lg:pb-4">
            {/* Main Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Row 1 */}
              <div className="order-last lg:order-1"></div>
              <div className="order-2 flex items-center justify-center mb-10 lg:mb-0">
                {/* Artwork Info */}
                <div className="flex flex-row justify-between gap-3">
                  <DashedLabel
                    label="artwork"
                    value={artImages && artImages.length > 0 ? artImages[currentImageIndex].title : 'Blue blossoms'}
                    id="chapter-image-info-title"
                  />
                  <DashedLabel
                    label="dimensions"
                    value={artImages && artImages.length > 0 ? artImages[currentImageIndex].dimensions : '80 x 100 cm'}
                    id="chapter-image-info-dimensions"
                  />
                </div>
              </div>
              <div className="order-auto lg:order-3"></div>

              {/* Row 2 - Tags */}
              <div className="order-4 lg:order-4 flex flex-col justify-center items-start p-4 py-6 lg:p-0">
                {layout.tags && layout.tags.length > 0 && (
                  <div className="flex flex-wrap flex-col gap-1 gap-y-3 max-w-lg">
                    {layout.tags?.map((tag, index1) => (
                      <div
                        key={index1}
                        className="flex text-[10px] grid-cols-2 flex-col lg:flex-row items-start lg:items-center gap-4 gap-y-1 lg:gap-y-20 mb-[2px] last:mb-0 lg:mb-0"
                      >
                        {tag.title && <span className="inline-flex min-w-[100px] uppercase">{tag.title}</span>}
                        <div>
                          {tag.labels?.map((label, index2) => (
                            <span
                              key={index2}
                              className={`px-1 py-[2px] inline-flex uppercase mr-[4px] rounded-sm mb-[4px] last:mb-0 lg:mb-0`}
                              style={{
                                backgroundColor: index1 % 2 === 0 ? '#000' : 'transparent',
                                color: index1 % 2 === 0 ? '#fff' : '#000',
                                border: '1px solid ' + (index1 % 2 === 0 ? '#fff' : '#000'),
                              }}
                            >
                              {label.title && <span className="font-bold">{label.title}</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Painting Preview Section */}
              <div
                className={`${
                  isFullscreen
                    ? 'fixed inset-0 z-20 w-screen h-screen'
                    : 'order-1 lg:order-4 flex items-center justify-center p-4 py-10 lg:py-4 lg:px-4 h-[60dvh]'
                }`}
              >
                {/* Fullscreen Toggle Button */}
                <button
                  onClick={toggleFullscreen}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    zIndex: 250,
                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    backdropFilter: 'blur(4px)',
                    color: 'white',
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease',
                  }}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                  className="hover:bg-black/40"
                >
                  {isFullscreen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
                      <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
                      <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
                      <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8V5a2 2 0 0 1 2-2h3"></path>
                      <path d="M19 8V5a2 2 0 0 0-2-2h-3"></path>
                      <path d="M8 21H5a2 2 0 0 1-2-2v-3"></path>
                      <path d="M21 16v3a2 2 0 0 1-2 2h-3"></path>
                    </svg>
                  )}
                </button>

                <Suspense fallback={null}>
                  <PaintingPreview
                    artImages={artImages}
                    currentImageIndex={currentImageIndex}
                    isFullscreen={isFullscreen}
                    showZoomHint={showZoomHint}
                  />
                </Suspense>
              </div>

              {/* Price Section */}
              <div className="flex items-center justify-center lg:justify-end order-5 lg:order-6 px-4 lg:px-0">
                <DashedPrice label="Reserved Price" value={layout.reservedPrice} className="mr-2" />
                <DashedPrice label="Buy Now Price" value={layout.buyowPrice} className="ml-2" />
              </div>

              {/* Row 3 - Image Navigation */}
              <div className="order-auto lg:order-7"></div>
              <div className="flex items-center justify-center order-3 lg:order-8">
                <ImageSlider
                  artImages={artImages}
                  isFullscreen={isFullscreen}
                  onFullscreenToggle={toggleFullscreen}
                  showZoomHint={showZoomHint}
                  onOpenFullscreen={openFullscreen}
                />
              </div>
              <div className="flex items-center justify-center lg:items-end lg:justify-end order-6 lg:order-9 p-2 lg:p-0">
                <div className={`flex justify-center items-center uppercase py-[8px] bg-[#F5F5F5] border-[#D9D9D9] border-[1px] border-dashed px-[8px] rounded-md`}>
                  <span className="text-[9px]">{layout.rightBottom}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Gallery View (Show All) */}
          <GalleryView
            artImages={artImages}
            showAllSection={showAllSection}
            onImageClick={openFullscreen}
          />

          {/* Fullscreen Image Viewer */}
          <FullscreenImageViewer
            fullscreenImage={fullscreenImage}
            onClose={closeFullscreen}
          />
        </>
      )}

      {layout.showVisions && (
        <>
          {/* Visions Section */}
          <div className="p-[15px] rounded-md bg-[#ACA280] text-black mb-[12px] font-light font-mono text-[12px] uppercase flex flex-row justify-between relative">
            <span>{layout.visionsTitle}</span>
            <span>{layout.visionsInfo}</span>
          </div>

          <div className="p-[15px] rounded-md bg-white text-black mb-[12px] pb-2 lg:pb-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Row 1 */}
              <div className="order-last lg:order-1"></div>
              <div className="order-last lg:order-1"></div>
              <div className="order-auto lg:order-3"></div>

              {/* Row 2 - Visions Tags */}
              <div className="order-4 lg:order-4 flex flex-col justify-center items-start p-4 py-6 lg:p-0">
                {layout.visionsTags && layout.visionsTags.length > 0 && (
                  <div className="flex flex-wrap flex-col gap-1 gap-y-3 max-w-lg">
                    {layout.visionsTags?.map((tag, index1) => (
                      <div
                        key={index1}
                        className="flex text-[10px] grid-cols-2 flex-col lg:flex-row items-start lg:items-center gap-4 gap-y-1 lg:gap-y-20 mb-[2px] last:mb-0 lg:mb-0"
                      >
                        {tag.title && <span className="inline-flex min-w-[100px] uppercase">{tag.title}</span>}
                        <div>
                          {tag.labels?.map((label, index2) => (
                            <span
                              key={index2}
                              className={`px-1 py-[2px] md:mb-0 inline-flex uppercase mr-[4px] rounded-sm mb-[4px] last:mb-0 lg:mb-0`}
                              style={{
                                backgroundColor: index1 % 2 === 0 ? '#000' : 'transparent',
                                color: index1 % 2 === 0 ? '#fff' : '#000',
                                border: '1px solid ' + (index1 % 2 === 0 ? '#fff' : '#000'),
                              }}
                            >
                              {label.title && <span className="font-bold">{label.title}</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video Player Section */}
              <div className="order-1 lg:order-4 flex items-center justify-center p-0 lg:py-0 pb-4 lg:px-14">
                {visionsVideos && visionsVideos.length > 0 && (
                  <div className="w-full flex items-center justify-center relative">
                    <video
                      ref={videoRef}
                      key={currentVideoIndex}
                      src={visionsVideos[currentVideoIndex].src}
                      className={`h-[80dvh] w-auto object-contain ${isVideoLoading ? 'opacity-0' : 'opacity-100'}`}
                      style={{ transition: 'opacity 0.3s ease' }}
                      autoPlay
                      loop
                      muted
                    />

                    {/* Loading indicator */}
                    {isVideoLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <MatrixText
                          phrases={['LOADING', 'VISIONS', 'LOADING']}
                          className="tracking-wider text-black text-[18px]"
                          interval={1500}
                          glitchProbability={0.05}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Visions Price Section */}
              <div className="flex items-center justify-center lg:justify-end order-5 lg:order-6">
                <DashedPrice label="Editions" value={layout.editions} className="mr-2" />
                <DashedPrice label="Buy Now Price" value={layout.visionsPrice} className="ml-2" />
              </div>

              {/* Row 3 - Change Artwork Button */}
              <div className="order-auto lg:order-7"></div>
              <div className="flex items-center justify-center order-3 lg:order-8">
                <div className="flex flex-col items-center w-full">
                  <button
                    className={`border border-dashed flex items-center mt-4 border-black pl-3 pr-4 py-[2px] rounded uppercase text-[12px] bg-[#F5F5F5] hover:bg-[#D9D9D9] transition-colors ${isChanging ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={isChanging ? undefined : handleRandomVideo}
                  >
                    {layout.changeVisionsText ? layout.changeVisionsText : 'CHANGE ARTWORK'}
                    <div className={`block w-3 h-3 pl-2`}>
                      <RefreshCw className={`block w-3 h-3 ${isVideoLoading ? 'animate-spin' : ''}`} />
                    </div>
                  </button>
                </div>
              </div>

              {/* Bottom Right Links */}
              <div className="flex flex-col items-stretch gap-2 order-6 lg:order-9 p-2 lg:p-0">
                {layout.visionsBottomRight &&
                  layout.visionsBottomRight.map((vision, index) => (
                    <div
                      key={index}
                      className={`flex justify-between items-center uppercase py-[8px] hover:bg-[#f0f0f0] transition-colors border-[#D9D9D9] border-[1px] border-dashed px-[8px] rounded-md whitespace-nowrap`}
                    >
                      <span className="text-[7px] md:text-[10px]">{vision.title}</span>
                      {vision.url && (
                        <Link href={vision.url} target="_blank" className="text-[10px]">
                          <span className="text-[10px] hidden sm:inline">DETAILS</span>→
                        </Link>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};